/**
 * tomboco-delivery-quote.js
 * Handles the delivery quote form and shipping display in the cart drawer.
 *
 * Include in theme.liquid before </body>:
 *   {{ 'tomboco-delivery-quote.js' | asset_url | script_tag }}
 */

window.tomboco = window.tomboco || {};

const TBC_FLAT_RATE = 15;            // Flat rate shipping (AUD) — update here when rate changes
const TBC_FREE_THRESHOLD = 300;      // Free shipping threshold (AUD)

(function () {

  // ─────────────────────────────────────────────
  // Read proxy URL
  // ─────────────────────────────────────────────
  function getProxyUrl() {
    const el = document.getElementById('tbc-proxy-url');
    return el ? el.dataset.url : null;
  }

  // ─────────────────────────────────────────────
  // Read cart data
  // ─────────────────────────────────────────────
  function getCartData() {
    const el = document.getElementById('tbc-cart-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { return null; }
  }

  // ─────────────────────────────────────────────
  // Shipping logic
  // ─────────────────────────────────────────────
  function getShippingInfo(data, fulfillment) {
    if (!data) return null;
    const items = data.items || [];

    const hasLarge    = items.some(i => i.shipping_large === true);
    const hasTextiles = items.some(i => i.shipping_textiles === true);
    const hasStandard = items.some(i => i.shipping_large !== true && i.shipping_textiles !== true);
    const subtotal    = (data.total_price || 0) / 100;

    if (fulfillment === 'pickup') {
      return { label: 'Pickup', display: 'Free', amount: 0, tbc: false };
    }

    if (hasLarge) {
      return { label: 'Delivery', display: 'TBC', amount: 0, tbc: true };
    }

    if (hasTextiles && !hasStandard) {
      return { label: 'Shipping', display: 'Free', amount: 0, tbc: false };
    }

    if (subtotal >= TBC_FREE_THRESHOLD) {
      return { label: 'Shipping', display: 'Free', amount: 0, tbc: false };
    }

    return { label: 'Shipping', display: '$' + TBC_FLAT_RATE, amount: TBC_FLAT_RATE };
  }

  // ─────────────────────────────────────────────
  // Format money (cents to AUD, no decimals)
  // ─────────────────────────────────────────────
  function formatMoney(cents) {
    return '$' + Math.round(cents / 100).toLocaleString('en-AU');
  }

  // ─────────────────────────────────────────────
  // Free shipping bar
  // ─────────────────────────────────────────────
  function injectFreeShippingBar(data) {
    document.querySelectorAll('.tbc-free-shipping-bar').forEach(el => el.remove());
    if (!data) return;

    const items       = data.items || [];
    const hasLarge    = items.some(i => i.shipping_large === true);
    const hasTextiles = items.some(i => i.shipping_textiles === true);
    const hasStandard = items.some(i => i.shipping_large !== true && i.shipping_textiles !== true);
    const subtotal    = (data.total_price || 0) / 100;

    // Don't show for large-item carts
    if (hasLarge) return;

    const bar = document.createElement('div');
    bar.className = 'tbc-free-shipping-bar';
    bar.style.cssText = 'padding: 12px 52px 10px; border-bottom: 1px solid rgba(255,255,255,0.06); box-sizing: border-box;';

    let message, pct;

    if (hasTextiles && !hasStandard) {
      // Textiles-only: always free, full bar
      message = 'You\'ve received free shipping';
      pct = 100;
    } else if (subtotal >= TBC_FREE_THRESHOLD) {
      message = 'You\'ve received free shipping';
      pct = 100;
    } else {
      const remaining = Math.ceil(TBC_FREE_THRESHOLD - subtotal);
      message = '$' + remaining + ' away from free shipping';
      pct = Math.round((subtotal / TBC_FREE_THRESHOLD) * 100);
    }

    bar.innerHTML = `
      <p style="font-size: 10px; font-weight: 300; letter-spacing: 0.18em; text-transform: uppercase; color: var(--color-foreground, #d4cfc9); margin: 0 0 8px; text-align: center;">${message}</p>
      <div style="height: 1px; background: rgba(255,255,255,0.1); overflow: hidden;">
        <div style="height: 100%; width: ${pct}%; background: var(--color-foreground, #d4cfc9); transition: width 0.4s ease;"></div>
      </div>`;

    // Insert at the top of the cart drawer scroll area
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const scrollArea = cartDrawer.querySelector('.cart-drawer__scrollable, [data-cart-scrollable], .scroll-area') || cartDrawer.firstElementChild;
    if (scrollArea) {
      scrollArea.insertBefore(bar, scrollArea.firstChild);
    }
  }

  // ─────────────────────────────────────────────
  // Update totals area
  // ─────────────────────────────────────────────
  function updateTotals(shippingInfo, data) {
    const totalRow   = document.getElementById('tbc-total-row');
    const totalPrice = document.getElementById('tbc-total-price');
    if (!totalRow || !totalPrice) return;

    document.querySelectorAll('.tbc-shipping-line').forEach(el => el.remove());

    if (!shippingInfo) return;

    const line = document.createElement('div');
    line.className = 'h-stack justify-between gap-4 tbc-shipping-line';
    line.innerHTML = `
      <span style="font-size: 13px; font-weight: 300; text-transform: uppercase; letter-spacing: 0.06em;">${shippingInfo.label}</span>
      <span style="font-size: 13px; font-weight: 300; min-width: 60px; text-align: right;">${shippingInfo.display}</span>
    `;
    totalRow.parentNode.insertBefore(line, totalRow);

    const gstTag = '<span style="font-size: 10px; font-weight: 300; letter-spacing: 0.1em; opacity: 0.7;">Incl. GST</span>';
    if (shippingInfo.tbc) {
      totalPrice.innerHTML = formatMoney(data.total_price) + ' ' + gstTag;
    } else {
      const totalCents = data.total_price + (shippingInfo.amount * 100);
      totalPrice.innerHTML = formatMoney(totalCents) + ' ' + gstTag;
    }
  }

  // ─────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────
  let currentFulfillment = 'delivery';

  function cartHasLarge(data) {
    if (!data) return false;
    return (data.items || []).some(i => i.shipping_large === true);
  }

  async function liveCartRequiresShipping() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return false;
      const cart = await res.json();
      const handles = [...new Set((cart.items || []).map(i => i.handle).filter(Boolean))];
      const results = await Promise.all(handles.map(handle =>
        fetch('/products/' + handle + '.js')
          .then(r => r.json())
          .then(p => (p.tags || []).indexOf('Shipping-Large') !== -1)
          .catch(() => false)
      ));
      return results.some(Boolean);
    } catch(e) {
      return false;
    }
  }

  // ─────────────────────────────────────────────
  // Apply fulfillment state
  // ─────────────────────────────────────────────
  function setQuoteFieldsRequired(enabled) {
    const quoteForm = document.getElementById('cart-drawer-delivery-quote-popover');
    if (!quoteForm) return;
    quoteForm.querySelectorAll('input[required], textarea[required]').forEach(el => {
      if (enabled) { el.setAttribute('required', ''); }
      else { el.removeAttribute('required'); }
    });
  }

  function applyFulfillmentState(data) {
    const buttonGroup = document.getElementById('tbc-checkout-buttons');
    const quoteWrap   = document.querySelector('.tbc-quote-wrap');
    const pickupNote  = document.querySelector('.tbc-pickup-note');
    const largeNote   = document.querySelector('.tbc-large-note');
    const hasLarge    = cartHasLarge(data);

    if (pickupNote) pickupNote.style.display = 'none';
    if (largeNote)  largeNote.style.display = 'none';

    if (currentFulfillment === 'pickup') {
      if (buttonGroup) buttonGroup.style.display = '';
      if (quoteWrap)   quoteWrap.style.display = 'none';
      setQuoteFieldsRequired(false);
    } else {
      if (hasLarge) {
        if (buttonGroup) buttonGroup.style.display = 'none';
        if (quoteWrap)   quoteWrap.style.display = '';
        if (largeNote)   largeNote.style.display = '';
        setQuoteFieldsRequired(true);
      } else {
        if (buttonGroup) buttonGroup.style.display = '';
        if (quoteWrap)   quoteWrap.style.display = 'none';
        if (pickupNote)  pickupNote.style.display = '';
        setQuoteFieldsRequired(false);
      }
    }

    updateTotals(getShippingInfo(data, currentFulfillment), data);
  }

  // ─────────────────────────────────────────────
  // Inject UI — toggle only for large carts
  // ─────────────────────────────────────────────
  function injectUI(hasLargeFromLive) {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;

    const buttonGroup = document.getElementById('tbc-checkout-buttons');
    const vStack = buttonGroup ? buttonGroup.closest('.v-stack') : null;
    if (!vStack) return;

    cartDrawer.querySelectorAll('.tbc-injected').forEach(el => el.remove());
    document.querySelectorAll('.tbc-shipping-line').forEach(el => el.remove());

    const data = getCartData();
    const hasLarge = cartHasLarge(data);

    if (hasLarge) {
      const toggle = document.createElement('div');
      toggle.className = 'tbc-fulfillment-toggle tbc-injected';
      toggle.innerHTML = `
        <p class="tbc-fulfillment-toggle__label">Please select your preferred delivery option</p>
        <div class="tbc-fulfillment-toggle__pills">
          <button type="button" class="tbc-fulfillment-pill${currentFulfillment === 'delivery' ? ' tbc-fulfillment-pill--active' : ''}" data-fulfillment="delivery">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
            Delivery
          </button>
          <button type="button" class="tbc-fulfillment-pill${currentFulfillment === 'pickup' ? ' tbc-fulfillment-pill--active' : ''}" data-fulfillment="pickup">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Pickup
          </button>
        </div>`;

      vStack.parentNode.insertBefore(toggle, vStack);

      toggle.querySelectorAll('.tbc-fulfillment-pill').forEach(pill => {
        pill.addEventListener('click', function () {
          currentFulfillment = pill.dataset.fulfillment;
          toggle.querySelectorAll('.tbc-fulfillment-pill').forEach(p => {
            p.classList.toggle('tbc-fulfillment-pill--active', p.dataset.fulfillment === currentFulfillment);
          });
          applyFulfillmentState(data);
        });
      });

      const quoteWrap = document.createElement('div');
      quoteWrap.className = 'tbc-injected tbc-quote-wrap';
      quoteWrap.innerHTML = `
        <button type="button" class="tbc-btn tbc-btn--full" aria-controls="cart-drawer-delivery-quote-popover" aria-expanded="false">
          <span class="tbc-btn__label">Request delivery quote</span>
        </button>`;
      vStack.appendChild(quoteWrap);

    } else {
      currentFulfillment = 'delivery';
    }

    injectFreeShippingBar(data);
    applyFulfillmentState(data);
  }

  // ─────────────────────────────────────────────
  // Main update
  // ─────────────────────────────────────────────
  async function updateUI() {
    const data = getCartData();
    const fromLiquid = cartHasLarge(data);
    injectUI(fromLiquid);

    const fromLive = await liveCartRequiresShipping();
    if (fromLive !== fromLiquid) {
      injectUI(fromLive);
    }
  }

  // ─────────────────────────────────────────────
  // Event listeners
  // ─────────────────────────────────────────────
  document.addEventListener('cart-drawer:refreshed', updateUI);
  document.addEventListener('shopify:section:load', updateUI);
  document.addEventListener('DOMContentLoaded', updateUI);

  // ─────────────────────────────────────────────
  // Form validation
  // ─────────────────────────────────────────────
  function validateForm() {
    const name    = document.getElementById('tbc-name');
    const email   = document.getElementById('tbc-email');
    const address = document.getElementById('tbc-address');
    const missing = [];
    if (!name    || !name.value.trim())    missing.push('Full name');
    if (!email   || !email.value.trim())   missing.push('Email address');
    if (!address || !address.value.trim()) missing.push('Delivery address');
    if (missing.length) {
      alert('Please complete the following fields:\n• ' + missing.join('\n• '));
      return false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.value.trim())) {
      alert('Please enter a valid email address.');
      return false;
    }
    return true;
  }

  // ─────────────────────────────────────────────
  // Submit handler
  // ─────────────────────────────────────────────
  window.tomboco.submitQuote = async function (btn) {
    if (!validateForm()) return;

    const proxyUrl = getProxyUrl();
    if (!proxyUrl) { showError(); return; }

    const cartData = getCartData();
    if (!cartData || !cartData.items.length) {
      alert('Your cart appears to be empty.');
      return;
    }

    const payload = {
      name:    document.getElementById('tbc-name').value.trim(),
      email:   document.getElementById('tbc-email').value.trim(),
      phone:   (document.getElementById('tbc-phone').value || '').trim(),
      address: document.getElementById('tbc-address').value.trim(),
      message: (document.getElementById('tbc-message').value || '').trim(),
      cart:    cartData
    };

    btn.disabled = true;
    btn.classList.add('tbc-btn--loading');
    hideError();

    try {
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Server returned ' + response.status);
      showSuccess();
      ['tbc-name', 'tbc-email', 'tbc-phone', 'tbc-address', 'tbc-message'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value = '';
      });
    } catch (err) {
      console.error('[Tomboco] Quote submission failed:', err);
      showError();
    } finally {
      btn.disabled = false;
      btn.classList.remove('tbc-btn--loading');
    }
  };

  // ─────────────────────────────────────────────
  // UI state helpers
  // ─────────────────────────────────────────────
  function showSuccess() {
    const success = document.getElementById('tbc-success');
    const error   = document.getElementById('tbc-error');
    const fields  = document.querySelector('.tbc-quote-fields');
    const submit  = document.querySelector('.tbc-quote-submit');
    const intro   = document.querySelector('.tbc-quote-intro');
    if (success) success.hidden = false;
    if (error)   error.hidden = true;
    if (fields)  fields.style.display = 'none';
    if (submit)  submit.style.display = 'none';
    if (intro)   intro.style.display = 'none';
  }

  function showError() {
    const e = document.getElementById('tbc-error');
    if (e) e.hidden = false;
  }

  function hideError() {
    const e = document.getElementById('tbc-error');
    if (e) e.hidden = true;
  }

})();